import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Loader2, LogOut, CreditCard, BarChart3, Settings, Key, History, Sparkles, ArrowRight, Palette } from 'lucide-react';
import { ApiKeyManager } from '@/components/ApiKeyManager';
import { ScanHistory } from '@/components/ScanHistory';
import { WhiteLabelSettings } from '@/components/WhiteLabelSettings';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import creditReportMoneyLogo from '@/assets/credit-report-money-logo.png';

interface Organization {
  id: string;
  name: string;
  slug: string;
  subscription_tier: string;
}

export default function Dashboard() {
  const { user, loading, signOut } = useAuth();
  const [org, setOrg] = useState<Organization | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [totalScans, setTotalScans] = useState<number>(0);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    async function fetchOrganization() {
      if (!user) return;

      try {
        const { data: membership, error: memberError } = await supabase
          .from('organization_members')
          .select('organization_id, role')
          .eq('user_id', user.id)
          .single();

        if (memberError || !membership) {
          navigate('/onboarding');
          return;
        }

        setIsAdmin(['owner', 'admin'].includes(membership.role));

        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', membership.organization_id)
          .single();

        if (orgError) throw orgError;

        setOrg(orgData);

        const [creditResult, scanResult] = await Promise.all([
          supabase.rpc('get_org_credit_balance', { _org_id: membership.organization_id }),
          supabase
            .from('scans')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', membership.organization_id)
        ]);

        setCredits(creditResult.data || 0);
        setTotalScans(scanResult.count || 0);
      } catch (error) {
        console.error('Error fetching organization:', error);
      } finally {
        setLoadingOrg(false);
      }
    }

    if (user) {
      fetchOrganization();
    }
  }, [user, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  if (loading || loadingOrg) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
        <div className="ambient-orb ambient-orb-primary absolute -top-[20%] -left-[10%] w-[50%] h-[50%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[10%] -right-[10%] w-[40%] h-[40%]" />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="ambient-orb ambient-orb-primary absolute -top-[20%] -left-[10%] w-[40%] h-[40%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[10%] -right-[10%] w-[30%] h-[30%]" />
        <div className="absolute inset-0 radar-grid opacity-10" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={creditReportMoneyLogo} 
              alt="Credit Report Money" 
              className="h-10 w-auto"
            />
            <span className="text-xl font-bold text-foreground hidden sm:block">{org?.name}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl glass-panel border border-accent/20">
              <CreditCard className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold text-accent">{credits} credits</span>
            </div>
            
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleSignOut}
              className="hover:bg-destructive/10 hover:text-destructive transition-colors"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8 animate-reveal">
          <h1 className="text-3xl font-bold">
            <span className="gradient-text-gold">Dashboard</span>
          </h1>
          <p className="text-muted-foreground mt-2">Manage your organization and analyze credit reports</p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="flashlight-card glass-panel rounded-2xl p-6 border border-border/50 animate-reveal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground">Total Scans</h3>
            </div>
            <p className="text-4xl font-bold text-foreground">{totalScans}</p>
            <p className="text-sm text-muted-foreground mt-1">All time</p>
          </div>

          <div className="flashlight-card glass-panel rounded-2xl p-6 border border-border/50 animate-reveal" style={{ animationDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-semibold text-foreground">Available Credits</h3>
            </div>
            <p className="text-4xl font-bold text-foreground">{credits}</p>
            <p className="text-sm text-muted-foreground mt-1">Scan credits remaining</p>
          </div>

          <div className="flashlight-card glass-panel rounded-2xl p-6 border border-border/50 animate-reveal" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-success/10 border border-success/20 flex items-center justify-center">
                <Settings className="h-6 w-6 text-success" />
              </div>
              <h3 className="font-semibold text-foreground">Subscription</h3>
            </div>
            <p className="text-4xl font-bold text-foreground capitalize">{org?.subscription_tier}</p>
            <p className="text-sm text-muted-foreground mt-1">Current plan</p>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="animate-reveal" style={{ animationDelay: '300ms' }}>
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="mb-6 glass-panel border border-border/50 p-1">
              <TabsTrigger value="overview" className="flex items-center gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                <BarChart3 className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="scan-history" className="flex items-center gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                <History className="h-4 w-4" />
                Scan History
              </TabsTrigger>
              <TabsTrigger value="api-keys" className="flex items-center gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                <Key className="h-4 w-4" />
                API Keys
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="white-label" className="flex items-center gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
                  <Palette className="h-4 w-4" />
                  White Label
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview">
              <div className="glass-panel-strong rounded-2xl p-12 text-center border border-border/50">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-6">
                  <Sparkles className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Ready to analyze credit reports?</h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  Your dashboard is set up and ready. Start analyzing credit reports or manage your API keys.
                </p>
                <Button 
                  className="btn-glow bg-gradient-to-r from-primary to-primary/90" 
                  size="lg" 
                  onClick={() => navigate('/analyze')}
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Start Analyzing
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="scan-history">
              <div className="glass-panel rounded-2xl border border-border/50 p-6">
                {org && <ScanHistory organizationId={org.id} />}
              </div>
            </TabsContent>

            <TabsContent value="api-keys">
              <div className="glass-panel rounded-2xl border border-border/50 p-6">
                {org && <ApiKeyManager organizationId={org.id} isAdmin={isAdmin} />}
              </div>
            </TabsContent>

            {isAdmin && (
              <TabsContent value="white-label">
                <div className="glass-panel rounded-2xl border border-border/50 p-6">
                  {org && <WhiteLabelSettings organizationId={org.id} isAdmin={isAdmin} />}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </main>
    </div>
  );
}
